import { AG_GRID_LOCALE_EN } from '@ag-grid-community/locale';
import { AgGridAngular } from 'ag-grid-angular';
import {
    AllCommunityModule,
    ClientSideRowModelModule,
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
    IRowNode,
    ModuleRegistry,
    RowGroupingDisplayType,
} from 'ag-grid-community';
import {
    ClipboardModule,
    ContextMenuModule,
    GroupFilterModule,
    PivotModule,
    RowGroupingModule,
    RowGroupingPanelModule,
    TreeDataModule,
} from 'ag-grid-enterprise';

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    OnInit,
} from '@angular/core';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import {
    AG_GRID_LOCALES,
    CURRENCY_VALUE_FORMATTER,
    numberColorCellClass,
    numberFieldComparator,
    numberFieldPropertyComparator,
    PERCENTAGE_VALUE_FORMATTER,
    stringComparator,
    WdxAgGridCurrencyField,
} from '@wdx/shared/components/ag-grid';
import {
    WdxButtonModule,
    WdxButtonStyle,
} from '@wdx/shared/components/wdx-button';
import { WdxDropdownModule } from '@wdx/shared/components/wdx-dropdown';
import { WdxIconModule } from '@wdx/shared/components/wdx-icon';
import { WdxSpinnerModule } from '@wdx/shared/components/wdx-spinner';
import {
    FriendlyProblemDetails,
    InvestmentDataConfiguration,
    InvestmentDataSectionType,
    SecurityPosition,
    TranslateTokenisedStringPipe,
    TranslationsService,
    WdxAgGridThemeDirective,
    WdxNumberService,
} from '@wdx/shared/utils';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { WdxSearchInputComponent } from '@wdx/shared/components/wdx-search-input';
import {
    PortfolioLoadingStates,
    PortfolioNotLoadedComponent,
} from '../portfolio-not-loaded/portfolio-not-loaded.component';

import * as R from 'ramda';
import { PortfolioAgGridColumnMapperService } from '../services/portfolio-ag-grid-column-mapper.service';

export interface WdxAgGridGroupByOption {
    label: string;
    columns: string[];
    autoGroupColumnDef?: ColDef;
}

export interface PositionsData extends Omit<SecurityPosition, 'price'> {
    price: WdxAgGridCurrencyField | null;
    cost: WdxAgGridCurrencyField;
    gainloss: WdxAgGridCurrencyField;
    value: WdxAgGridCurrencyField;
}

ModuleRegistry.registerModules([
    AllCommunityModule,
    ClientSideRowModelModule,
    GroupFilterModule,
    PivotModule,
    RowGroupingModule,
    RowGroupingPanelModule,
    TreeDataModule,
    ClipboardModule,
    ContextMenuModule,
]);

@Component({
    selector: 'wdx-portfolio-positions',
    imports: [
        AgGridAngular,
        WdxAgGridThemeDirective,
        WdxSpinnerModule,
        WdxDropdownModule,
        WdxIconModule,
        NgbDropdownModule,
        WdxButtonModule,
        WdxSearchInputComponent,
        PortfolioNotLoadedComponent,
        TranslateTokenisedStringPipe,
    ],
    providers: [PortfolioAgGridColumnMapperService],
    templateUrl: './positions.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'd-block h-100' },
})
export class PortfolioPositionsComponent implements OnInit {
    private numberService = inject(WdxNumberService);
    private changeDetectorRef = inject(ChangeDetectorRef);
    private translationsService = inject(TranslationsService);
    private agGridColumnMapperService = inject(
        PortfolioAgGridColumnMapperService,
    );

    @Input() portfolioConfig?: InvestmentDataConfiguration;
    @Input() dataTypeLabel = 'Position';
    @Input() dataConnectionError?: FriendlyProblemDetails = undefined;
    @Input() localeCode?: string | null | undefined;
    @Input() set positionsData(value: PositionsData[] | null | undefined) {
        if (!value) {
            this.status = 'loading';
            return;
        }

        this.status = 'loaded';
        this.rowData = value;
        if (!this.rowData.length) {
            this.status = 'noData';
        }
        this.filteredRowData = [...this.rowData];
        this.changeDetectorRef.markForCheck();
    }

    public status: PortfolioLoadingStates = 'loading';
    public readonly WdxButtonStyle = WdxButtonStyle;
    public selectedGroupby = 0;
    public loading = true;

    public groupByOptions: WdxAgGridGroupByOption[] = [
        {
            label: 'PORTFOLIO_POSITIONS_GROUP_BY_NONE',
            columns: [],
            autoGroupColumnDef: undefined,
        },
        {
            label: 'PORTFOLIO_POSITIONS_GROUP_BY_ASSET_CLASS',
            columns: ['assetClass'],
            autoGroupColumnDef: {
                headerName: this.translationsService.translateTokenisedString(
                    'PORTFOLIO_POSITIONS_ASSET_CLASS',
                ),
                cellRendererParams: {
                    suppressCount: true,
                    checkbox: false,
                },
            },
        },
    ];

    private rowData?: PositionsData[];
    public filteredRowData?: PositionsData[];
    public autoGroupColumnDef =
        this.groupByOptions[this.selectedGroupby].autoGroupColumnDef;
    public groupDisplayType: RowGroupingDisplayType = 'groupRows';

    public gridOptions: GridOptions = {
        domLayout: 'autoHeight',
        pagination: true,
        paginationPageSize: 20,
        defaultColDef: {
            flex: 1,
            sortable: true,
            minWidth: 75,
            contextMenuItems: ['copy'],
        },
        onGridReady: this.onGridReady.bind(this),
    };

    public colDefs?: ColDef[];
    private allColDefs: ColDef[] = [
        {
            headerName: this.translationsService.translateTokenisedString(
                'PORTFOLIO_POSITIONS_NAME',
            ),
            field: 'name',
            flex: 2,
            pinned: 'left',
            lockPinned: true,
            minWidth: 125,
            comparator: stringComparator,
        },
        {
            headerName: this.translationsService.translateTokenisedString(
                'PORTFOLIO_POSITIONS_IDENTIFIER',
            ),
            field: 'identifier',
            comparator: stringComparator,
        },
        {
            headerName: this.translationsService.translateTokenisedString(
                'PORTFOLIO_POSITIONS_AMOUNT',
            ),
            field: 'amount',
            minWidth: 70,
        },
        {
            headerName: this.translationsService.translateTokenisedString(
                'PORTFOLIO_POSITIONS_ASSET_CLASS',
            ),
            field: 'assetClass',
            rowGroup: false,
            hide: false,
        },
        {
            headerName: this.translationsService.translateTokenisedString(
                'PORTFOLIO_POSITIONS_PRICE',
            ),
            headerClass: 'ag-right-aligned-header',
            field: 'price',
            cellClass: 'text-end',
            minWidth: 115,
            valueGetter: (params) =>
                R.isNil(params.data?.priceAsPercentage)
                    ? { type: 'currency', value: params.data?.price }
                    : {
                          type: 'percentage',
                          value: params.data?.priceAsPercentage,
                      },
            comparator: (_: any, __: any, a: IRowNode, b: IRowNode) => {
                const fieldA =
                    a.data?.priceAsPercentage !== null
                        ? a.data?.priceAsPercentage
                        : a.data?.price?.value;
                const fieldB =
                    b.data?.priceAsPercentage !== null
                        ? b.data?.priceAsPercentage
                        : b.data?.price?.value;
                return fieldA - fieldB;
            },
            valueFormatter: (params) => {
                return params.value?.type === 'percentage'
                    ? PERCENTAGE_VALUE_FORMATTER(params.value)
                    : CURRENCY_VALUE_FORMATTER(this.numberService)(
                          params.value,
                      );
            },
        },
        {
            headerName: this.translationsService.translateTokenisedString(
                'PORTFOLIO_POSITIONS_BOOK_COST',
            ),
            headerClass: 'ag-right-aligned-header',
            field: 'cost',
            cellClass: 'text-end',
            minWidth: 115,
            comparator: numberFieldPropertyComparator('cost', 'value'),
            valueFormatter: CURRENCY_VALUE_FORMATTER(this.numberService),
        },
        {
            headerName: this.translationsService.translateTokenisedString(
                'PORTFOLIO_POSITIONS_CHANGE',
            ),
            field: 'gainloss',
            headerClass: 'ag-right-aligned-header',
            cellClass: numberColorCellClass('text-end', 'gainloss', 'value'),
            minWidth: 115,
            comparator: numberFieldPropertyComparator('gainloss', 'value'),
            valueFormatter: CURRENCY_VALUE_FORMATTER(this.numberService),
        },
        {
            headerName: this.translationsService.translateTokenisedString(
                'PORTFOLIO_POSITIONS_PERCENT_CHANGE',
            ),
            field: 'gainLossPercentage',
            minWidth: 80,
            maxWidth: 100,
            headerClass: 'ag-right-aligned-header',
            cellClass: 'text-end',
            valueFormatter: PERCENTAGE_VALUE_FORMATTER,
            comparator: numberFieldComparator('gainLossPercentage'),
        },
        {
            headerName: this.translationsService.translateTokenisedString(
                'PORTFOLIO_POSITIONS_MARKET_VALUE',
            ),
            headerClass: 'ag-right-aligned-header',
            field: 'value',
            cellClass: 'text-end',
            minWidth: 100,
            comparator: numberFieldPropertyComparator('value', 'value'),
            valueFormatter: CURRENCY_VALUE_FORMATTER(this.numberService),
        },
    ];

    private gridApi?: GridApi;

    ngOnInit(): void {
        const lang = this.localeCode?.split('-')[0] || 'en';
        this.gridOptions.localeText =
            AG_GRID_LOCALES[lang] || AG_GRID_LOCALE_EN;

        const colConfig = this.portfolioConfig?.widgets?.find(
            ({ name }) => name === 'PortfolioPositions',
        );

        const primaryColumnsInConfig = colConfig?.columns?.filter(
            ({ sectionType }) =>
                sectionType === InvestmentDataSectionType.Primary,
        );

        if (primaryColumnsInConfig) {
            // TODO: below is intentionally left commented out, we need to do further work before
            // enabling dynamic column configuration for positions, with these the groupby does not work
            // we either need to make that configurable as well or remove it
            // this.colDefs = primaryColumnsInConfig.map((col) =>
            //     this.agGridColumnMapperService.createColDef(col),
            // );
            this.colDefs = R.clone(this.allColDefs);
        } else {
            this.colDefs = R.clone(this.allColDefs);
        }
    }

    public onGroupBySelection(
        selection: WdxAgGridGroupByOption,
        index: number,
    ) {
        if (index !== this.selectedGroupby && this.gridApi) {
            this.selectedGroupby = index;
            const colDefs = this.gridApi.getColumnDefs() as ColDef[];
            colDefs.forEach((colDef) => {
                const group = selection.columns.includes(
                    colDef.field as string,
                );
                colDef.hide = group;
                colDef.rowGroup = group;
            });
            this.autoGroupColumnDef = selection.autoGroupColumnDef;
            this.gridApi.setGridOption('columnDefs', []);
            // settimeout required to ensure the column headers update properly
            // we need to set the columnDefs to empty array, then allow time for that apply before setting our desired config
            setTimeout(() => {
                this.gridApi?.setGridOption('columnDefs', colDefs);
                this.gridApi?.refreshCells();
            }, 0);
        }
    }

    public onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;
    }

    public search(searchTerm: string) {
        if (!searchTerm) {
            this.filteredRowData = [...(this.rowData || [])];
            return;
        }
        const cleanedSearch = searchTerm.trim().toLowerCase();
        this.filteredRowData = this.rowData?.filter(
            (row) =>
                row?.assetClass?.toLowerCase().includes(cleanedSearch) ||
                row?.identifier?.toLowerCase().includes(cleanedSearch) ||
                row?.name?.toLowerCase().includes(cleanedSearch),
        );
    }
}
